/**
 * eExact XML generator for booking products (boekingsproducten).
 * 
 * Generates XML files that are byte-identical in structure to the
 * LSDecoProf order exports used by Luijten-VVZ BV.
 */

export interface EExactOrderLine {
  /** Booking product_id (e.g. "103-627QO0ZN-1500") */
  itemCode: string;
  /** Color/product description for the <Text> element */
  text?: string;
  /** Quantity ordered (will be 3-digit zero-padded) */
  quantity: number;
}

export interface EExactOrder {
  /** Order number from Lightspeed (e.g. "VO146337") */
  ordernumber: string;
  /** Product lines (booking products) */
  lines: EExactOrderLine[];
}

// Fixed configuration — always the same for all exports
const DEBTOR_CODE = '106131';
const YOUR_REF = 'LSDecoProf order Luijten-VVZ BV';
const WAREHOUSE_CODE = '2';
const HEADER_ITEM = '999-A0000021-0001';

function padQuantity(qty: number): string {
  return String(qty).padStart(3, '0');
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function generateOrderLines(order: EExactOrder, lineNo: number): { xml: string; nextLineNo: number } {
  let xml = '';

  // Order header line
  xml += `<OrderLine lineNo="${lineNo}">`;
  xml += `<Item code="${HEADER_ITEM}"/>`;
  xml += `<Text>ORDER ${escapeXml(order.ordernumber)}</Text>`;
  xml += `<Quantity>${padQuantity(1)}</Quantity>`;
  xml += `</OrderLine>`;
  lineNo++;

  // Booking product lines
  for (const line of order.lines) {
    xml += `<OrderLine lineNo="${lineNo}">`;
    xml += `<Item code="${escapeXml(line.itemCode)}"/>`;
    if (line.text) {
      xml += `<Text>${escapeXml(line.text)}</Text>`;
    }
    xml += `<Quantity>${padQuantity(line.quantity)}</Quantity>`;
    xml += `</OrderLine>`;
    lineNo++;
  }

  return { xml, nextLineNo: lineNo };
}

/**
 * Generate a complete eExact XML document with all orders in a single <Order> block.
 */
export function generateEExactXml(orders: EExactOrder[]): string {
  let xml = '\ufeff';
  xml += `<eExact xlmnsxsi="http://www.w3.org/2001/XMLSchema-instance" xsinoNamespaceSchemaLocation="eExact-Schema.xsd">`;
  xml += `<Orders>`;
  xml += `<Order invoicemethod="F" type="V">`;
  xml += `<Warehouse code="${WAREHOUSE_CODE}"/>`;
  xml += `<YourRef>${escapeXml(YOUR_REF)}</YourRef>`;
  xml += `<OrderedBy><Debtor code="${DEBTOR_CODE}" number="${DEBTOR_CODE}" type="C"/></OrderedBy>`;
  xml += `<DeliverTo><Debtor code="${DEBTOR_CODE}" number="${DEBTOR_CODE}" type="C"/></DeliverTo>`;
  xml += `<InvoiceTo><Debtor code="${DEBTOR_CODE}" number="${DEBTOR_CODE}" type="C"/></InvoiceTo>`;

  let lineNo = 1;
  for (const order of orders) {
    const result = generateOrderLines(order, lineNo);
    xml += result.xml;
    lineNo = result.nextLineNo;
  }

  xml += `</Order>`;
  xml += `</Orders>`;
  xml += `</eExact>`;

  return xml;
}

export function generateEExactFilename(): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  return `LSDecoProf_order_Luijten-VVZ_BV_-_${date}_${time}_-_001_-_01.XML`;
}

export function downloadEExactXml(xmlContent: string, filename?: string): void {
  const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || generateEExactFilename();
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
